import { decodeNpub, fetchLatestEvent } from "./nostr";
import { qrToCanvas } from "./qr";
import { onPage } from "./router";

const NPUB = "npub1ven4zk8xxw873876gx8y9g9l9fazkye9qnwnglcptgvfwxmygscqsxddfh";
const RELAYS = [
  "wss://relay.damus.io",
  "wss://nos.lol",
  "wss://relay.ditto.pub",
  "wss://relay.primal.net",
  "wss://nostr.wine",
];

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T;
const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

let pubkeyHex = "";
try {
  pubkeyHex = decodeNpub(NPUB);
} catch (e) {
  console.error("bad npub", e);
}

/* ════════════ PIXEL STARFIELD ════════════ */
function starfield(signal: AbortSignal) {
  const c = $<HTMLCanvasElement>("#stars");
  const ctx = c.getContext("2d")!;
  const colors = ["#00ffa3", "#2bd9ff", "#9fb6cc", "#9fb6cc", "#ff2e88"];
  type Star = { x: number; y: number; s: number; v: number; col: string };
  let stars: Star[] = [];

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(140, Math.floor((c.width * c.height) / 14000));
    stars = Array.from({ length: count }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      s: Math.random() > 0.9 ? 2 : 1,
      v: 0.15 + Math.random() * 0.5,
      col: colors[(Math.random() * colors.length) | 0]!,
    }));
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const st of stars) {
      ctx.globalAlpha = 0.5 + Math.random() * 0.5;
      ctx.fillStyle = st.col;
      ctx.fillRect(st.x | 0, st.y | 0, st.s, st.s);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (const st of stars) {
      st.y -= st.v;
      if (st.y < -4) {
        st.y = c.height + 4;
        st.x = Math.random() * c.width;
      }
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

/* ════════════ DEFAULT PIXEL AVATAR ════════════ */
function drawDefaultAvatar() {
  const c = $<HTMLCanvasElement>("#avatar");
  const ctx = c.getContext("2d")!;
  const S = 16;
  const px = c.width / S;
  const map = [
    "................",
    "....11111111....",
    "...1111111111...",
    "..111111111111..",
    "..11g11111g111..",
    "..111111111111..",
    "..11g111111g11..",
    "..111gggggg111..",
    "...1111111111...",
    "....11mmmm11....",
    "...11mmmmmm11...",
    "..1mm1mmmm1mm1..",
    ".1mm11mmmm11mm1.",
    ".1m111mmmm111m1.",
    "....mm....mm....",
    "...mm......mm...",
  ];
  const colors: Record<string, string> = { "1": "#1a1030", g: "#00ffa3", m: "#3a1140" };
  ctx.fillStyle = "#0a0712";
  ctx.fillRect(0, 0, c.width, c.height);
  for (let y = 0; y < S; y++)
    for (let x = 0; x < S; x++) {
      const ch = map[y]?.[x] ?? ".";
      if (ch === "." || ch === " ") continue;
      ctx.fillStyle = colors[ch] ?? "#2bd9ff";
      ctx.fillRect(x * px, y * px, px, px);
    }
}

function loadAvatar(url: string) {
  const c = $<HTMLCanvasElement>("#avatar");
  const ctx = c.getContext("2d")!;
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    const N = 56;
    const tmp = document.createElement("canvas");
    tmp.width = N;
    tmp.height = N;
    const tctx = tmp.getContext("2d")!;
    tctx.imageSmoothingEnabled = false;
    const side = Math.min(img.width, img.height);
    tctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, N, N);
    ctx.imageSmoothingEnabled = false;

    const drawNew = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.drawImage(tmp, 0, 0, N, N, 0, 0, c.width, c.height);
    };
    if (REDUCED) {
      drawNew();
      return;
    }

    // cross-fade from whatever is on the canvas now (the default pixel avatar)
    const old = document.createElement("canvas");
    old.width = c.width;
    old.height = c.height;
    old.getContext("2d")!.drawImage(c, 0, 0);

    const DUR = 600;
    const start = performance.now();
    const fade = (now: number) => {
      const t = Math.min(1, (now - start) / DUR);
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.globalAlpha = 1;
      ctx.drawImage(old, 0, 0);
      ctx.globalAlpha = t;
      ctx.drawImage(tmp, 0, 0, N, N, 0, 0, c.width, c.height);
      ctx.globalAlpha = 1;
      if (t < 1) requestAnimationFrame(fade);
      else drawNew();
    };
    requestAnimationFrame(fade);
  };
  img.onerror = () => drawDefaultAvatar();
  img.src = url;
}

/* ════════════ TYPEWRITER ════════════ */
let typeTimer: ReturnType<typeof setTimeout> | undefined;
function typeText(el: HTMLElement, text: string) {
  clearTimeout(typeTimer);
  const current = el.textContent ?? "";
  if (current === text) return;
  if (REDUCED) {
    el.textContent = text;
    return;
  }
  // backspace to the common prefix, then type the rest
  let p = 0;
  while (p < current.length && p < text.length && current[p] === text[p]) p++;
  let i = current.length;
  const type = () => {
    el.textContent = text.slice(0, ++i);
    if (i < text.length) typeTimer = setTimeout(type, 18 + Math.random() * 40);
  };
  const del = () => {
    if (i > p) {
      el.textContent = current.slice(0, --i);
      typeTimer = setTimeout(del, 10 + Math.random() * 18);
    } else if (i < text.length) {
      typeTimer = setTimeout(type, 160);
    }
  };
  del();
}

/* ════════════ COPY ════════════ */
async function copyToClipboard(text: string, selectEl?: HTMLElement) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    if (selectEl) {
      const r = document.createRange();
      r.selectNode(selectEl);
      getSelection()?.removeAllRanges();
      getSelection()?.addRange(r);
    }
  }
}

function flashCopied(btn: HTMLElement) {
  const old = btn.textContent;
  btn.textContent = "✓ COPIED";
  btn.classList.add("copied");
  setTimeout(() => {
    btn.textContent = old;
    btn.classList.remove("copied");
  }, 1500);
}

function wireCopy(btn: HTMLElement, getText: () => string, selectEl?: HTMLElement) {
  btn.addEventListener("click", async () => {
    await copyToClipboard(getText(), selectEl);
    flashCopied(btn);
  });
}

/* ════════════ STATIC SETUP ════════════ */
function setupStatic() {
  $("#npub").textContent = NPUB;
  $<HTMLAnchorElement>("#follow").href = `https://ditto.pub/${NPUB}`;
  $<HTMLAnchorElement>("#njump").href = `https://njump.me/${NPUB}`;

  const npubEl = $("#npub");
  wireCopy($("#copy"), () => NPUB, npubEl);
  wireCopy($("#copy-hero"), () => NPUB, npubEl);
  wireCopy($("#copy-foot"), () => NPUB, npubEl);
  npubEl.addEventListener("click", () => $("#copy").click());
  npubEl.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") $("#copy").click();
  });

  // payment cards: each .pay has a .paycode + a COPY button
  document.querySelectorAll<HTMLElement>(".pay").forEach((pay) => {
    const code = pay.querySelector<HTMLElement>(".paycode");
    const btn = pay.querySelector<HTMLButtonElement>("button");
    if (!code || !btn) return;
    const text = (code.textContent ?? "").trim();
    wireCopy(btn, () => text, code);
    code.addEventListener("click", () => btn.click());
    code.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") btn.click();
    });
  });

  try {
    qrToCanvas($<HTMLCanvasElement>("#qr"), `nostr:${NPUB}`, {
      margin: 1,
      width: 400,
      dark: "#00ffa3",
      light: "#03050a",
    });
  } catch (e) {
    console.error("qr", e);
  }
}

/* ════════════ NOSTR ════════════ */
function fetchNostr() {
  const status = $("#status");
  if (!pubkeyHex) {
    status.textContent = "✖ identity decode failed";
    return;
  }
  fetchLatestEvent(RELAYS, { kinds: [0], authors: [pubkeyHex], limit: 1 })
    .then((ev) => {
      status.classList.add("online");
      status.textContent = "● online on nostr";
      if (!ev) return;
      try {
        const meta = JSON.parse(ev.content);
        if (meta.about) typeText($("#tagline"), (String(meta.about).split("\n")[0] ?? "").slice(0, 120));
        if (meta.picture) loadAvatar(meta.picture);
      } catch {
        /* keep defaults */
      }
    })
    .catch(() => {
      status.textContent = "⚠ relays unreachable";
    });
}

/* ════════════ GO ════════════ */
onPage((signal) => {
  starfield(signal);
  drawDefaultAvatar();
  setupStatic();
  $("#tagline").textContent = "";
  typeText($("#tagline"), "cypherpunk · builder of freedom tools");
  fetchNostr();
});
